import { useMemo, useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { ErrorMessage } from '@calendly/form'
import { Button } from '@calendly/ui/components/button'
import { useDispatch, useSelector } from 'react-redux'

import { eventTypeSelector } from 'root-api'
import { Settings } from 'services/settings'
import RoutesHelper from 'services/asset-path'
import { useI18n } from 'locales'
import { useProductRefreshUiTheme } from '../../../product_refresh/useProductRefreshUiTheme'

import { embedSelector } from '../../../selectors/scheduling'
import { stripeClientSecretSelector } from '../../../selectors/payment'
import { fetchClientSecret } from '../../enter_details/payment/thunks'

import { getStripeElementsTextAppearanceFromQuery } from './stripeAppearance'

const Container = ({ stripeAccount, children }) => {
  const { i18n } = useI18n()
  const isProductRefreshUiTheme = useProductRefreshUiTheme()
  const [error, setError] = useState(null)
  const stripePromise = useMemo(() => loadStripe(Settings.stripe.publishable_key, { stripeAccount }), [stripeAccount])
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const { status: clientSecretStatus } = stripeClientSecret
  const dispatch = useDispatch()
  const { color_customization: embedCustomColors } = useSelector(embedSelector)

  const { uuid: eventTypeUuid } = useSelector(eventTypeSelector)

  const textAppearance = embedCustomColors ? getStripeElementsTextAppearanceFromQuery() : undefined

  // Depends on the status so a PaymentIntent that was discarded mid-booking (e.g. a redirect
  // payment method that came back to a scheduling error) is replaced with a fresh one. `error`
  // is deliberately excluded to keep a failing endpoint from being polled.
  useEffect(() => {
    if (clientSecretStatus !== 'ready') return
    dispatch(fetchClientSecret(eventTypeUuid))
  }, [clientSecretStatus, dispatch, eventTypeUuid])

  useEffect(() => {
    async function awaitStripePromise() {
      try {
        await stripePromise
        setError(null)
      } catch (e) {
        setError(i18n.t('errors.billing.card.payment_form_not_loaded'))
      }
    }
    awaitStripePromise()
  }, [i18n, stripePromise])

  if (error) {
    return <ErrorMessage message={error} />
  }

  if (clientSecretStatus === 'error') {
    return (
      <>
        <ErrorMessage message={i18n.t('errors.billing.card.payment_form_not_loaded')} />
        <Button onClick={() => dispatch(fetchClientSecret(eventTypeUuid))}>
          {i18n.t('errors.messages.try_again')}
        </Button>
      </>
    )
  }

  if (clientSecretStatus !== 'loaded') return <>Loading...</>

  const fontCssSrc = isProductRefreshUiTheme
    ? RoutesHelper.assetUrl('external/brand-fonts.css')
    : RoutesHelper.assetUrl('external/proxima-nova.css')

  const options = {
    locale: i18n.locale,
    fonts: [{ cssSrc: fontCssSrc }],
    ...(textAppearance ? { appearance: textAppearance } : {}),
    clientSecret: stripeClientSecret.value
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  )
}

export default Container
